import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dumbbell, Flame, Trophy, Apple, ChefHat, CheckCircle2, Circle,
  ChevronLeft, ChevronRight, Scale, TrendingUp, Sparkles, Plus, Trash2, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAB_PROGRAM = 'program';
const TAB_MEALS = 'meals';
const TAB_MEASUREMENTS = 'measurements';

/* =========================================================
   Workout Day Card — shown inline on selected week
   ========================================================= */
function WorkoutDayCard({ phase, week, day, doneMap, onToggle, toggling }) {
  const dayKey = String(day.day_index);
  const done = Boolean((doneMap || {})[dayKey]);

  return (
    <div
      className={`neo-card p-4 transition ${done ? 'opacity-80' : ''}`}
      style={{ background: done ? '#E6FFE0' : 'white' }}
      data-testid={`workout-day-card-${day.day_index}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{day.emoji}</span>
            <div>
              <div className="text-[10px] font-bold opacity-60 uppercase">Dag {day.day_index + 1}</div>
              <h4 className="font-bold text-sm leading-tight">{day.focus}</h4>
            </div>
          </div>
        </div>
        <button
          onClick={() => onToggle(week, day.day_index, !done)}
          disabled={toggling}
          className={`w-10 h-10 shrink-0 rounded-xl border-[3px] border-black flex items-center justify-center transition shadow-[2px_2px_0_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${
            done ? 'bg-[#B9FBC0]' : 'bg-white'
          }`}
          data-testid={`workout-toggle-${week}-${day.day_index}`}
          aria-label={done ? 'Ångra klar' : 'Markera klar'}
        >
          {done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
        </button>
      </div>

      <ul className="space-y-1.5 mt-2" data-testid="workout-exercise-list">
        {(day.exercises || []).map((ex, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-xs border-l-2 border-black pl-2 py-0.5"
          >
            <span className="font-bold opacity-60 shrink-0">{i + 1}.</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-bold">{ex.name}</span>
                <span className="text-[10px] bg-[#FFE8A3] border border-black px-1.5 py-0.5 rounded-full">
                  {ex.sets}
                </span>
              </div>
              {ex.notes && <p className="text-[11px] opacity-70 leading-snug">{ex.notes}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* =========================================================
   Program Tab
   ========================================================= */
function ProgramTab({ data, onRefresh, onPointsChange }) {
  const phases = data.phases || [];
  const splits = data.splits || {};
  const dayNames = data.day_names || [];
  const completions = data.completions || {};
  const [selectedWeek, setSelectedWeek] = useState(data.current_week || 1);
  const [togglingKey, setTogglingKey] = useState(null);

  const currentPhase = phases.find(p => p.weeks.includes(selectedWeek));
  const phaseId = currentPhase?.id;
  const daysForPhase = splits[String(phaseId)] || [];
  const weekDone = completions[String(selectedWeek)] || {};
  const doneCount = Object.keys(weekDone).length;
  const pct = Math.round((doneCount / 7) * 100);

  // Total progress: workouts done across all 16 weeks / 112 possible
  const totalDone = Object.values(completions).reduce((s, w) => s + Object.keys(w || {}).length, 0);
  const totalPct = Math.round((totalDone / 112) * 100);

  const toggle = async (week, dayIdx, done) => {
    const key = `${week}-${dayIdx}`;
    setTogglingKey(key);
    try {
      if (done) {
        const { data: resp } = await axios.post(
          `${API}/body/workout/${week}/${dayIdx}/complete`,
          {}, { withCredentials: true }
        );
        if (resp.already_completed) {
          toast('Redan klart ✨');
        } else {
          if (resp.bonus > 0) {
            toast.success(`+${resp.points_awarded}p! ${resp.bonus_reason || ''}`.trim());
          } else {
            toast.success(`✅ Klart! +${resp.points_awarded}p · Streak: ${resp.streak}`);
          }
        }
      } else {
        await axios.delete(
          `${API}/body/workout/${week}/${dayIdx}`, { withCredentials: true }
        );
        toast('Ångrad');
      }
      await onRefresh();
      if (onPointsChange) onPointsChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setTogglingKey(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-2" data-testid="body-stats-strip">
        <div className="neo-card p-3 bg-[#FFE8A3] text-center">
          <Flame className="w-5 h-5 mx-auto mb-1" />
          <div className="text-2xl font-black leading-none">{data.streak || 0}</div>
          <div className="text-[10px] font-bold uppercase opacity-70">Streak</div>
        </div>
        <div className="neo-card p-3 bg-[#B9FBC0] text-center">
          <Trophy className="w-5 h-5 mx-auto mb-1" />
          <div className="text-2xl font-black leading-none">{data.total_workouts || 0}</div>
          <div className="text-[10px] font-bold uppercase opacity-70">Pass klara</div>
        </div>
        <div className="neo-card p-3 bg-[#C8B6FF] text-center">
          <Sparkles className="w-5 h-5 mx-auto mb-1" />
          <div className="text-2xl font-black leading-none">{totalPct}%</div>
          <div className="text-[10px] font-bold uppercase opacity-70">Av 16 v</div>
        </div>
      </div>

      {/* Phase header */}
      {currentPhase && (
        <div
          className="neo-card p-4"
          style={{ background: `linear-gradient(135deg, ${currentPhase.color}, #FFE8F0)` }}
          data-testid="current-phase-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-3xl">{currentPhase.emoji}</span>
            <div>
              <h3 className="font-black text-lg leading-tight">{currentPhase.name}</h3>
              <p className="text-xs opacity-70">Vecka {currentPhase.weeks[0]}–{currentPhase.weeks[3]} · {currentPhase.duration_min} min/dag</p>
            </div>
          </div>
          <p className="text-sm italic text-black/80">"{currentPhase.tagline}"</p>
          <p className="text-xs mt-1"><strong>Fokus:</strong> {currentPhase.focus}</p>
        </div>
      )}

      {/* Week selector */}
      <div className="neo-card p-3 bg-white" data-testid="week-selector">
        <div className="flex items-center justify-between gap-2 mb-2">
          <button
            onClick={() => setSelectedWeek(Math.max(1, selectedWeek - 1))}
            disabled={selectedWeek === 1}
            className="neo-btn p-2 bg-white disabled:opacity-40"
            data-testid="week-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 text-center">
            <div className="text-[10px] font-bold uppercase opacity-60">Vecka</div>
            <div className="text-2xl font-black">{selectedWeek} / 16</div>
          </div>
          <button
            onClick={() => setSelectedWeek(Math.min(16, selectedWeek + 1))}
            disabled={selectedWeek === 16}
            className="neo-btn p-2 bg-white disabled:opacity-40"
            data-testid="week-next-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="h-2 w-full border-[2px] border-black rounded-full overflow-hidden bg-white">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${pct}%`,
              background: pct === 100 ? 'linear-gradient(90deg,#B9FBC0,#C8B6FF,#FFC8DD)' : '#B9FBC0',
            }}
          />
        </div>
        <div className="text-xs text-center opacity-70 mt-1">{doneCount} / 7 dagar klara</div>
      </div>

      {/* Day cards for selected week */}
      <div className="grid gap-3">
        {daysForPhase.map((day) => (
          <WorkoutDayCard
            key={day.day_index}
            phase={currentPhase}
            week={selectedWeek}
            day={day}
            doneMap={weekDone}
            onToggle={toggle}
            toggling={togglingKey === `${selectedWeek}-${day.day_index}`}
          />
        ))}
      </div>

      {/* Phase grid overview */}
      <div className="neo-card p-3 bg-white" data-testid="phase-overview">
        <h4 className="font-bold text-sm mb-2">16 veckor översikt</h4>
        <div className="grid grid-cols-4 gap-1.5">
          {phases.map((ph) => (
            <div key={ph.id} className="border-[2px] border-black rounded-lg p-2" style={{ background: ph.color }}>
              <div className="text-xl text-center">{ph.emoji}</div>
              <div className="text-[10px] font-bold text-center leading-tight">Fas {ph.id}</div>
              <div className="text-[9px] text-center opacity-70">v{ph.weeks[0]}-{ph.weeks[3]}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   Meals Tab
   ========================================================= */
function MealsTab({ data }) {
  if (!data) return <div className="text-center py-6 text-sm opacity-60">Laddar...</div>;

  return (
    <div className="space-y-4" data-testid="meals-tab">
      {/* Principles */}
      <div>
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <ChefHat className="w-4 h-4" /> 8 grundregler
        </h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {(data.principles || []).map((p, i) => (
            <div key={i} className="neo-card p-3 bg-white" data-testid="meal-principle">
              <div className="flex items-start gap-2">
                <span className="text-2xl shrink-0">{p.emoji}</span>
                <div>
                  <h4 className="font-bold text-sm">{p.title}</h4>
                  <p className="text-xs opacity-80 mt-0.5">{p.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 7-day meal plan */}
      <div>
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <Apple className="w-4 h-4" /> 7-dagars färdig meny
        </h3>
        <div className="grid gap-2">
          {(data.week_plan || []).map((d, i) => (
            <div key={i} className="neo-card p-3 bg-white" data-testid="meal-day-card">
              <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full border-[2px] border-black bg-[#FFE8A3] text-xs">
                  {d.day}
                </span>
              </h4>
              <ul className="space-y-1.5 text-xs">
                {Object.entries(d.meals).map(([meal, desc]) => (
                  <li key={meal} className="flex gap-2">
                    <span className="font-bold min-w-[56px]">{meal}:</span>
                    <span className="flex-1 opacity-85">{desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Go-to meals */}
      <div>
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Gå-till-måltider (ADHD-vänliga)
        </h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {(data.go_to_meals || []).map((m, i) => (
            <div key={i} className="neo-card p-3 bg-[#FDF0D5]" data-testid="goto-meal">
              <div className="flex items-start gap-2">
                <span className="text-2xl shrink-0">{m.emoji}</span>
                <div>
                  <h4 className="font-bold text-sm">{m.name}</h4>
                  <p className="text-xs opacity-80 mt-0.5">{m.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   Measurements Tab
   ========================================================= */
function MeasurementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [w, setW] = useState('');
  const [waist, setWaist] = useState('');
  const [hip, setHip] = useState('');
  const [note, setNote] = useState('');

  const fetchList = async () => {
    try {
      const { data } = await axios.get(`${API}/body/measurements`, { withCredentials: true });
      setItems(data.measurements || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchList(); }, []);

  const submit = async () => {
    const payload = {};
    if (w) payload.weight_kg = Number(w);
    if (waist) payload.waist_cm = Number(waist);
    if (hip) payload.hip_cm = Number(hip);
    if (note) payload.note = note;
    if (!Object.keys(payload).length) {
      toast.error('Fyll i minst ett fält');
      return;
    }
    try {
      await axios.post(`${API}/body/measurement`, payload, { withCredentials: true });
      toast.success('📊 Mätning sparad');
      setW(''); setWaist(''); setHip(''); setNote('');
      setShowForm(false);
      fetchList();
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Ta bort mätning?')) return;
    try {
      await axios.delete(`${API}/body/measurement/${id}`, { withCredentials: true });
      fetchList();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const first = items[0];
  const latest = items[items.length - 1];
  const diff = (k) => {
    if (!first || !latest || first.id === latest.id) return null;
    const a = first[k]; const b = latest[k];
    if (a == null || b == null) return null;
    const d = +(b - a).toFixed(1);
    return { a, b, d };
  };

  return (
    <div className="space-y-4" data-testid="measurements-tab">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-bold flex items-center gap-2">
          <Scale className="w-4 h-4" /> Mina mätningar
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="neo-btn px-3 py-1.5 bg-[#C8B6FF] font-bold text-xs flex items-center gap-1"
          data-testid="add-measurement-btn"
        >
          {showForm ? <><X className="w-3.5 h-3.5" /> Stäng</> : <><Plus className="w-3.5 h-3.5" /> Ny mätning</>}
        </button>
      </div>

      {showForm && (
        <div className="neo-card p-3 bg-white space-y-2" data-testid="measurement-form">
          <div className="grid grid-cols-3 gap-2">
            <input type="number" step="0.1" placeholder="Vikt (kg)" value={w}
              onChange={e => setW(e.target.value)} className="neo-input text-sm" data-testid="m-weight" />
            <input type="number" step="0.1" placeholder="Midja (cm)" value={waist}
              onChange={e => setWaist(e.target.value)} className="neo-input text-sm" data-testid="m-waist" />
            <input type="number" step="0.1" placeholder="Höft (cm)" value={hip}
              onChange={e => setHip(e.target.value)} className="neo-input text-sm" data-testid="m-hip" />
          </div>
          <input type="text" placeholder="Anteckning (valfri)" value={note}
            onChange={e => setNote(e.target.value)} className="neo-input text-sm w-full" maxLength={200} data-testid="m-note" />
          <button onClick={submit} className="neo-btn w-full py-2 bg-[#B9FBC0] font-bold text-sm" data-testid="m-save">
            Spara
          </button>
        </div>
      )}

      {/* Diff card */}
      {items.length > 1 && (
        <div className="neo-card p-3 bg-gradient-to-br from-[#FFE8F0] to-[#E8F5FF]" data-testid="measurement-diff">
          <h4 className="font-bold text-xs mb-2 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5" /> Förändring sedan start
          </h4>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {['weight_kg', 'waist_cm', 'hip_cm'].map((k) => {
              const d = diff(k);
              if (!d) return <div key={k} className="opacity-40 text-center">—</div>;
              const label = k === 'weight_kg' ? 'Vikt' : k === 'waist_cm' ? 'Midja' : 'Höft';
              const unit = k === 'weight_kg' ? 'kg' : 'cm';
              const color = d.d < 0 ? '#B9FBC0' : d.d > 0 ? '#FFC8DD' : 'white';
              return (
                <div key={k} className="border-2 border-black rounded-lg p-2 text-center" style={{ background: color }}>
                  <div className="font-bold">{label}</div>
                  <div className="text-lg font-black">{d.d > 0 ? '+' : ''}{d.d} {unit}</div>
                  <div className="text-[10px] opacity-60">{d.a} → {d.b}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-6 text-sm opacity-60">Laddar...</div>
      ) : items.length === 0 ? (
        <div className="neo-card p-6 bg-white text-center text-sm opacity-60">
          Inga mätningar ännu. Logga din startpunkt nu!
        </div>
      ) : (
        <div className="space-y-1.5">
          {[...items].reverse().map((m) => (
            <div key={m.id} className="neo-card p-2.5 bg-white flex items-center gap-2 text-xs" data-testid="measurement-item">
              <div className="flex-1 min-w-0">
                <div className="font-bold">
                  {new Date(m.logged_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                <div className="flex gap-3 opacity-80">
                  {m.weight_kg != null && <span>⚖️ {m.weight_kg} kg</span>}
                  {m.waist_cm != null && <span>📏 Midja {m.waist_cm}</span>}
                  {m.hip_cm != null && <span>🍑 Höft {m.hip_cm}</span>}
                </div>
                {m.note && <div className="opacity-60 italic mt-0.5">{m.note}</div>}
              </div>
              <button onClick={() => del(m.id)} className="neo-btn p-1.5 bg-white" data-testid="m-delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Main
   ========================================================= */
export default function BodyPage({ onPointsChange }) {
  const [tab, setTab] = useState(TAB_PROGRAM);
  const [programData, setProgramData] = useState(null);
  const [mealsData, setMealsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProgram = async () => {
    try {
      const { data } = await axios.get(`${API}/body/program`, { withCredentials: true });
      setProgramData(data);
    } catch (e) {
      console.error(e);
    }
  };
  const fetchMeals = async () => {
    try {
      const { data } = await axios.get(`${API}/body/meals`, { withCredentials: true });
      setMealsData(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchProgram(), fetchMeals()]);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="body-page">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFC8DD] via-[#FFE8F0] to-[#A2D2FF]">
        <h2 className="font-bold text-lg flex items-center gap-2">
          <Dumbbell className="w-5 h-5" /> Kropp — 16 veckor av helvete 💀✨
        </h2>
        <p className="text-xs text-black/70 mt-1">
          4 faser × 4 veckor. Mommy makeover-effekt utan kirurgi: platt mage 💫 lyft rumpa 🍑 toneade armar 💪 hållning 🦩.
          Hemma, utan utrustning, 30-45 min/dag.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="body-subtabs">
        {[
          { id: TAB_PROGRAM, label: 'Program', icon: Dumbbell, color: '#FFC8DD' },
          { id: TAB_MEALS, label: 'Mat', icon: Apple, color: '#B9FBC0' },
          { id: TAB_MEASUREMENTS, label: 'Mätning', icon: Scale, color: '#C8B6FF' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`neo-btn px-3 py-2 flex items-center gap-1.5 text-xs whitespace-nowrap ${
              tab === t.id ? '' : 'bg-white'
            }`}
            style={tab === t.id ? { background: t.color } : {}}
            data-testid={`body-subtab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-sm opacity-60">Laddar programmet...</div>
      ) : (
        <>
          {tab === TAB_PROGRAM && programData && (
            <ProgramTab data={programData} onRefresh={fetchProgram} onPointsChange={onPointsChange} />
          )}
          {tab === TAB_MEALS && <MealsTab data={mealsData} />}
          {tab === TAB_MEASUREMENTS && <MeasurementsTab />}
        </>
      )}
    </div>
  );
}
